import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Scale } from "lucide-react";
import api from "@/lib/api";
import Seo, { faqSchema, breadcrumbSchema, orgSchema } from "@/components/Seo";
import WhatsAppCTA from "@/components/WhatsAppCTA";

const SITE = "https://signiastylettoix.com";

export default function CompareLanding() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setPage(null);
        api.get(`/compare-pages/${slug}`)
            .then((r) => { if (!cancelled) setPage(r.data); })
            .catch(() => { if (!cancelled) setError(true); });
        window.scrollTo(0, 0);
        return () => { cancelled = true; };
    }, [slug]);

    if (error) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Comparison not found. <Link className="underline ml-2" to="/compare">Open the compare tool</Link></div>;
    if (!page) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const url = `${SITE}/compare/${page.slug}`;
    const [a, b] = page.products;
    const schema = {
        "@context": "https://schema.org",
        "@graph": [orgSchema(), faqSchema(page.faqs || []),
            breadcrumbSchema([{ name: "Home", url: SITE + "/" }, { name: "Compare", url: SITE + "/compare" }, { name: page.h1, url }])],
    };

    return (
        <>
            <Seo title={page.title} description={page.meta_description} canonical={url} schema={schema} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to="/compare" className="hover:text-[#003E4D]">Compare</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{page.h1}</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2"><Scale size={12} /> Signia Comparison · 2026</p>
                    <h1 className="font-serif text-[34px] md:text-[48px] leading-[1.1] mt-4" data-testid="compare-h1">{page.h1}</h1>
                    <div className="mt-5 text-[16.5px] text-[#4a5568] max-w-[780px]" dangerouslySetInnerHTML={{ __html: page.intro }} />

                    <div className="mt-10 overflow-x-auto max-w-[880px]">
                        <table className="w-full text-left text-[15px]" data-testid="compare-table">
                            <thead>
                                <tr className="border-b-2 border-[#003E4D]">
                                    <th className="py-3 pr-4">Feature</th>
                                    <th className="py-3 pr-4">{a.name}</th>
                                    <th className="py-3">{b.name}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {page.rows.map((r, i) => (
                                    <tr key={i} className="border-b border-[#efece6]">
                                        <td className="py-3 pr-4 font-medium">{r[0]}</td>
                                        <td className="py-3 pr-4">{r[1]}</td>
                                        <td className="py-3">{r[2]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-10 max-w-[820px]">
                        <h2 className="font-serif text-[28px] text-[#003E4D] mb-3">Our verdict</h2>
                        <div data-testid="verdict" className="text-[16px] text-[#333] leading-relaxed" dangerouslySetInnerHTML={{ __html: page.verdict }} />
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to={`/products/${a.slug}`} className="btn-primary" data-testid="compare-view-a">View {a.name} <ArrowRight size={15} /></Link>
                        <Link to={`/products/${b.slug}`} className="btn-primary" data-testid="compare-view-b">View {b.name} <ArrowRight size={15} /></Link>
                        <WhatsAppCTA message={`Hi, I'm deciding between the ${a.name} and ${b.name}. Can you help?`} sourcePage={url} className="btn-whatsapp">Trial Both Free</WhatsAppCTA>
                    </div>

                    <div className="mt-12 max-w-[820px]">
                        <h2 className="font-serif text-[26px] text-[#003E4D] mb-4">Frequently asked questions</h2>
                        {page.faqs.map((f, i) => (
                            <details key={i} className="bg-white border border-[#e2ddd4] rounded-xl px-5 py-4 my-2">
                                <summary className="cursor-pointer font-medium text-[15.5px]">{f.q}</summary>
                                <p className="mt-2 text-[15px] text-[#4a5568]">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
